"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleAlert as AlertCircle, Clock, PhoneCall, ShieldAlert, ArrowDown } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function EmergencyGuide() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const highlights = [{
    icon: Clock,
    title: "Immediate action",
    desc: "Stop water, cut power if wet, and evacuate unsafe zones."
  }, {
    icon: ShieldAlert,
    title: "Document everything",
    desc: "Take clear photos and video before moving any items."
  }, {
    icon: PhoneCall,
    title: "Call local dispatch",
    desc: "Reach on-call crews across MD, DC, and Northern Virginia."
  }];
  return <section id="emergencyguidehero" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground pt-32 pb-20 md:pt-40 md:pb-28 border-b border-border/20">
      {/* Subtle architectural background texture */}
      <div className="absolute inset-0 pointer-events-none opacity-10 bg-[radial-gradient(circle_at_top_right,var(--color-accent)_0%,transparent_60%)]" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-secondary-foreground/20 to-transparent" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-5xl">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex flex-col items-center text-center space-y-6">
          {/* Status Badge */}
          <motion.div variants={itemVariants}>
            <Badge variant="outline" className="border-accent/40 bg-accent/10 text-accent gap-1.5 py-1 px-3 text-xs tracking-wide uppercase font-medium rounded-full">
              <AlertCircle className="size-3.5 text-accent" />
              24/7 Property Response Protocol
            </Badge>
          </motion.div>

          {/* Main Title */}
          <motion.h1 variants={itemVariants} className="font-default text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-secondary-foreground max-w-3xl">
            Emergency Restoration Guide
          </motion.h1>

          {/* Subheadline */}
          <motion.p variants={itemVariants} className="font-default text-lg sm:text-xl text-secondary-foreground/85 max-w-2xl text-balance leading-relaxed">
            What to do in the first minutes after a property emergency.
          </motion.p>

          {/* Direct CTA Group */}
          <motion.div variants={itemVariants} className="flex flex-wrap items-center justify-center gap-4 pt-3">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-medium px-7 shadow-md">
              <Link to="/ContactRequestServicePage">Request 24/7 dispatch</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-secondary-foreground/30 bg-secondary/60 text-secondary-foreground hover:bg-secondary hover:text-secondary-foreground px-6">
              <Link to="#step-by-step">
                Read checklist <ArrowDown className="ml-1.5 size-4" />
              </Link>
            </Button>
          </motion.div>

          {/* Quick Critical Checklist Cards */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full pt-12 text-left">
            {highlights.map((item, index) => {
            const IconComp = item.icon;
            return <div key={item.title} data-index={index} className="rounded-lg border border-secondary-foreground/15 bg-secondary-foreground/5 p-5 backdrop-blur-xs transition-colors hover:border-secondary-foreground/30">
                  <div className="flex items-center gap-3 mb-2.5">
                    <div className="p-2 rounded-md bg-secondary text-accent border border-secondary-foreground/20">
                      <IconComp className="size-4" />
                    </div>
                    <h2 className="font-default text-base font-semibold text-secondary-foreground">
                      {item.title}
                    </h2>
                  </div>
                  <p className="font-default text-sm text-secondary-foreground/75 leading-snug">
                    {item.desc}
                  </p>
                </div>;
          })}
          </motion.div>
        </motion.div>
      </div>
    </section>;
}